import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from 'react-bootstrap'
import { FaRegSquarePlus } from "react-icons/fa6";
import CreateCategory from './CreateCategory'
import * as yup from 'yup';
import { useFormik } from 'formik';
import toast from 'react-hot-toast'
import { createCategory, deleteCategory, editCategory, getCategoryListingPaginate, statusUpdateCategory } from '../../api/context/category'
import imageToBase64 from 'image-to-base64/browser';
import moment from 'moment';
import { BiSolidEditAlt } from "react-icons/bi";
import { MdDelete } from "react-icons/md";
import { FiArrowRight, FiPlus } from "react-icons/fi";
import CustomisedModal from '../../components/Modal/CustomisedModal'
import { checkUserPermissions } from '../../helpers/permission'
import Loader from '../../components/loader/loader'
import { Switch } from '@mui/material';

const PAGE_LIMIT = 1000;

const Categories = () => {

    const navigate = useNavigate();

    const [show, setShow] = useState(false);
    const handleShow = () => setShow(true);

    const [loading, setLoading] = useState(false);
    const [update, setUpdate] = useState(false);
    const [category, setCategory] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [modal, setModal] = useState(false);
    const [deletingId, setDeletingId] = useState("");

    const user = JSON.parse(localStorage.getItem('user') || 'null');
    const isVendor = user?.userType === 'Vendor';
    const canManage = checkUserPermissions('manage_category');
    const canViewSubs = checkUserPermissions('view_sub_category');

    const toggleModal = () => setModal(prev => !prev);

    const handleClose = () => {
        formik.resetForm();
        setShow(false);
        setSelectedCategory(null);
    };

    const validationSchema = yup.object().shape({
        title: yup.string('').trim().required('Name is required'),
        desc: yup.string('').trim().required('Description is required'),
        img: selectedCategory?.id ? yup.string().nullable() : yup.string().required('Image is required'),
        isActive: yup.boolean().required('isActive is required'),
    });

    const formik = useFormik({
        initialValues: {
            subCategoryId: '',
            title: '',
            desc: '',
            img: '',
            img_view: '',
            isActive: true
        },
        validationSchema,
        onSubmit: async (values, { resetForm }) => {
            setLoading(true);
            if (values.img === '') delete values.img;
            delete values.img_view;

            const onDone = (successMsg) => (response) => {
                if (response && response?.data?.statusCode === 200) {
                    resetForm();
                    toast.success(successMsg);
                    handleClose();
                    setUpdate(u => !u);
                    setSelectedCategory(null);
                } else {
                    toast.error(response?.data?.message);
                }
                setLoading(false);
            };

            if (selectedCategory?.id) {
                editCategory(values)(onDone("Category Updated Successfully"));
            } else {
                createCategory(values)(onDone("Category Created Successfully"));
            }
        },
    });

    const handleImageChange = async (e) => {
        const file = e?.target?.files[0];
        const maxSizeInBytes = 2 * 1024 * 1024;

        if (file?.size > maxSizeInBytes) {
            toast.error("File size exceeds 2 MB limit", 'error');
            e.target.value = null;
            return;
        }

        const response = await imageToBase64(URL.createObjectURL(file));
        formik.setValues({
            ...formik.values,
            img: response,
            img_view: 'data:image/jpeg;base64,' + response,
        });
    };

    const getAllCategories = () => {
        setLoading(true);
        getCategoryListingPaginate({ page: 1, limit: PAGE_LIMIT })((response) => {
            if (response?.statusCode === 200) {
                setCategory(response?.data?.subcategories || []);
            } else {
                setCategory([]);
            }
            setLoading(false);
        });
    };

    const handleEditClick = (data) => {
        setSelectedCategory(data);
        formik.setValues({
            ...formik.values,
            subCategoryId: String(data?.id ?? ''),
            title: data?.title ?? '',
            desc: data?.description ?? '',
            img_view: data?.img ?? '',
            isActive: data?.isActive ?? true
        });
        setShow(true);
    };

    const handleConfirm = (id) => {
        setModal(true);
        setDeletingId(id);
    };

    const statusActive = (id) => {
        const previous = [...category];
        setCategory(prev =>
            prev.map(item =>
                item.id === id ? { ...item, isActive: !item.isActive } : item
            )
        );
        statusUpdateCategory(id)((response) => {
            if (response?.data?.statusCode !== 200) {
                setCategory(previous);
                toast.error("Failed to update status");
            }
        });
    };

    const deleteCategories = (id) => {
        deleteCategory(id)((response) => {
            if (response?.data?.statusCode === 200) {
                toast.success("Category deleted successfully");
                setModal(false);
                setUpdate(u => !u);
            } else {
                toast.error(response?.data?.message);
            }
        });
    };

    useEffect(() => {
        getAllCategories();
    }, [update]);

    return (
        <div className="categories-page">
            <div className="page-header-sticky d-sm-flex justify-content-between align-items-start flex-wrap gap-3">
                <div>
                    <h3 className="mb-1 fw-bold">
                        {isVendor ? 'Explore Categories' : 'Categories'}
                    </h3>
                    <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                        {isVendor
                            ? 'Browse categories to add products to your inventory.'
                            : 'Organize your product catalog and keep categories up to date.'}
                    </p>
                </div>

                {canManage && !isVendor && (
                    <Button className="request_btn" onClick={handleShow}>
                        <FiPlus size={18} strokeWidth={3} />
                        Add New Category
                    </Button>
                )}
            </div>

            <div className="row">
                <div className="col-12">

                    {loading ? (
                        <Loader />
                    ) : (
                        <div className="categories-grid">
                            {category?.length > 0 ? (
                                category.map((data, index) => (
                                    <div className="category-card" key={data?.id ?? index}>
                                        <div className="card-media">
                                            {data?.img ? (
                                                <img src={data.img} alt={data?.title} />
                                            ) : (
                                                <div className="media-placeholder">No image</div>
                                            )}

                                            {canManage && (
                                                <button
                                                    type="button"
                                                    className="edit-btn"
                                                    onClick={() => handleEditClick(data)}
                                                    title="Edit"
                                                >
                                                    <BiSolidEditAlt className="icon" />
                                                    <span>Edit</span>
                                                </button>
                                            )}
                                        </div>

                                        <div className="card-body">
                                            <div className="card-title-row">
                                                <h3>{data?.title}</h3>
                                                {canManage && (
                                                    <span className="status-inline">
                                                        <Switch
                                                            size="small"
                                                            checked={!!data?.isActive}
                                                            onChange={() => statusActive(data?.id)}
                                                            className="custom-switch"
                                                        />
                                                    </span>
                                                )}
                                            </div>

                                            {canViewSubs && (
                                                <button
                                                    type="button"
                                                    className="view-sub-btn"
                                                    onClick={() => navigate('/sub-categories/' + data?.id, { state: { data } })}
                                                >
                                                    <span>View Sub Categories</span>
                                                    <FiArrowRight className="arrow-icon" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="categories-empty">
                                    No categories found
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <CreateCategory
                    show={show}
                    handleClose={handleClose}
                    handleShow={handleShow}
                    formik={formik}
                    handleImageChange={handleImageChange}
                    selectedCategory={selectedCategory}
                    loading={loading}
                />

                <CustomisedModal
                    isOpen={modal}
                    customClass={"policy_modal"}
                    toggle={toggleModal}
                >
                    <h5>Are you sure you want to remove this item?</h5>
                    <div className="policy_Agree">
                        <Button
                            onClick={() => setModal(false)}
                            size="lg"
                            className="btn_cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            size="lg"
                            className="btn"
                            onClick={() => deleteCategories(deletingId)}
                        >
                            Remove
                        </Button>
                    </div>
                </CustomisedModal>
            </div>
        </div>
    );
};

export default Categories;
